import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAuth } from '@/lib/auth'
import {
  type DashboardNavGroupId,
  type DashboardNavItemId,
  canAccessDashboardPath,
  clampDashboardSidebarWidth,
  dashboardNavGroups,
  dashboardRailWidth,
  dashboardSidebarDefaultWidth,
  defaultDashboardNavGroupId,
  getDashboardDefaultPath,
  getDashboardMainWidth,
  getVisibleDashboardNavGroups,
  itemActive,
} from '@/lib/dashboard-shell'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { cn } from '@/lib/utils'
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import {
  Building2,
  BellRing,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Database,
  FileText,
  Files,
  FolderTree,
  LayoutDashboard,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  PlugZap,
  Settings,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  TableProperties,
  UserCog,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

const sidebarStorageKey = 'miaoform.dashboard.sidebar'

const navGroupIcons: Record<DashboardNavGroupId, LucideIcon> = {
  workspace: LayoutDashboard,
  forms: FileText,
  customers: Building2,
  erp: Database,
  admin: ShieldCheck,
  integrations: PlugZap,
  system: Settings,
}

const navItemIcons: Record<DashboardNavItemId, LucideIcon> = {
  dashboard: LayoutDashboard,
  forms: FileText,
  formTemplates: Files,
  questionGroups: FolderTree,
  customerApplications: ClipboardList,
  reviewCenter: ClipboardCheck,
  customers: Building2,
  reviewNotifications: BellRing,
  erpCustomerFields: TableProperties,
  admins: ShieldCheck,
  sales: UserCog,
  settings: Settings,
  integrations: PlugZap,
  systemNavigation: SlidersHorizontal,
  systemShare: Share2,
  systemForms: FileText,
  systemCustomers: Building2,
  systemRuntime: Settings,
}

function getStoredSidebarState() {
  if (typeof window === 'undefined') {
    return {
      activeGroupId: defaultDashboardNavGroupId,
      collapsed: false,
      sidebarWidth: dashboardSidebarDefaultWidth,
    }
  }

  try {
    const raw = window.localStorage.getItem(sidebarStorageKey)
    if (!raw) {
      return {
        activeGroupId: defaultDashboardNavGroupId,
        collapsed: false,
        sidebarWidth: dashboardSidebarDefaultWidth,
      }
    }
    const value = JSON.parse(raw) as {
      activeGroupId?: string
      collapsed?: boolean
      sidebarWidth?: number
    }
    const activeGroupId = dashboardNavGroups.some((group) => group.id === value.activeGroupId)
      ? (value.activeGroupId as DashboardNavGroupId)
      : defaultDashboardNavGroupId
    return {
      activeGroupId,
      collapsed: Boolean(value.collapsed),
      sidebarWidth: clampDashboardSidebarWidth(
        typeof value.sidebarWidth === 'number' ? value.sidebarWidth : dashboardSidebarDefaultWidth,
      ),
    }
  } catch {
    return {
      activeGroupId: defaultDashboardNavGroupId,
      collapsed: false,
      sidebarWidth: dashboardSidebarDefaultWidth,
    }
  }
}

export default function DashboardLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, isLoading } = useAuth()
  const initialSidebarState = useMemo(() => getStoredSidebarState(), [])
  const [activeGroupId, setActiveGroupId] = useState<DashboardNavGroupId>(
    initialSidebarState.activeGroupId,
  )
  const [subnavCollapsed, setSubnavCollapsed] = useState(initialSidebarState.collapsed)
  const [subnavWidth, setSubnavWidth] = useState(initialSidebarState.sidebarWidth)
  const [isResizingSubnav, setIsResizingSubnav] = useState(false)
  const featureVisibility = useSystemFeatureVisibility()

  const visibleGroups = useMemo(
    () => getVisibleDashboardNavGroups(user, featureVisibility),
    [featureVisibility, user],
  )

  const routeGroupId = useMemo(() => {
    return visibleGroups.find((group) =>
      group.items.some((item) => itemActive(location.pathname, item)),
    )?.id
  }, [location.pathname, visibleGroups])

  useEffect(() => {
    if (routeGroupId) {
      setActiveGroupId((current) => (current === routeGroupId ? current : routeGroupId))
    }
  }, [routeGroupId])

  useEffect(() => {
    if (!isLoading && !user) {
      void navigate({ to: '/login' })
    }
  }, [isLoading, navigate, user])

  useEffect(() => {
    if (isLoading || !user) return
    if (canAccessDashboardPath(user, location.pathname)) return

    void navigate({ to: getDashboardDefaultPath(user), replace: true })
  }, [isLoading, location.pathname, navigate, user])

  useEffect(() => {
    window.localStorage.setItem(
      sidebarStorageKey,
      JSON.stringify({ activeGroupId, collapsed: subnavCollapsed, sidebarWidth: subnavWidth }),
    )
  }, [activeGroupId, subnavCollapsed, subnavWidth])

  const handleSubnavResizeStart = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      event.preventDefault()
      const startX = event.clientX
      const startWidth = subnavWidth
      setIsResizingSubnav(true)

      const handlePointerMove = (moveEvent: PointerEvent) => {
        setSubnavWidth(clampDashboardSidebarWidth(startWidth + moveEvent.clientX - startX))
      }

      const handlePointerUp = () => {
        setIsResizingSubnav(false)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        window.removeEventListener('pointermove', handlePointerMove)
        window.removeEventListener('pointerup', handlePointerUp)
      }

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp, { once: true })
    },
    [subnavWidth],
  )

  const activeGroup =
    visibleGroups.find((group) => group.id === activeGroupId) ??
    visibleGroups[0] ??
    dashboardNavGroups[0]
  const dashboardMainWidth = getDashboardMainWidth({
    collapsed: subnavCollapsed,
    sidebarWidth: subnavWidth,
  })
  const dashboardGridTemplateColumns = subnavCollapsed
    ? `${dashboardRailWidth}px 0px minmax(0, 1fr)`
    : `${dashboardRailWidth}px ${subnavWidth}px minmax(0, 1fr)`

  if (isLoading || !user) {
    return <div className="grid min-h-dvh place-items-center text-sm text-slate-500">載入中...</div>
  }

  async function handleLogout() {
    await logout()
    await navigate({ to: '/login' })
  }

  function selectGroup(groupId: DashboardNavGroupId) {
    setActiveGroupId(groupId)
    setSubnavCollapsed(false)
  }

  return (
    <div
      className={cn(
        'h-dvh overflow-hidden bg-slate-50 text-slate-950 lg:grid',
        isResizingSubnav
          ? 'select-none'
          : 'transition-[grid-template-columns] duration-200 ease-out',
      )}
      style={
        {
          '--miao-dashboard-main-width': dashboardMainWidth,
          gridTemplateColumns: dashboardGridTemplateColumns,
        } as CSSProperties
      }
    >
      <div className="flex h-14 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 lg:hidden">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-950">
            {user?.name ?? '內部使用者'}
          </div>
          <div className="truncate text-xs text-slate-500">{user?.email}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout} className="shrink-0 gap-2">
          <LogOut className="size-4" />
          登出
        </Button>
      </div>

      <aside className="hidden h-dvh border-r border-slate-200 bg-white lg:flex lg:min-h-0 lg:flex-col">
        <div className="flex h-16 shrink-0 items-center justify-center border-b border-slate-200">
          <div className="grid size-10 place-items-center rounded-xl bg-indigo-600 font-black text-white">
            M
          </div>
        </div>

        <ScrollArea className="min-h-0 flex-1" viewportClassName="px-2 py-3">
          <nav className="flex flex-col gap-2">
            {visibleGroups.map((group) => {
              const Icon = navGroupIcons[group.id]
              const active = activeGroup.id === group.id
              return (
                <button
                  key={group.id}
                  data-testid={`dashboard-rail-${group.id}`}
                  type="button"
                  title={group.label}
                  aria-label={group.label}
                  aria-pressed={active}
                  onClick={() => selectGroup(group.id)}
                  className={cn(
                    'grid size-11 place-items-center rounded-xl text-slate-500 transition-colors hover:bg-slate-50 hover:text-indigo-700',
                    active && 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100',
                  )}
                >
                  <Icon className="size-5" />
                </button>
              )
            })}
          </nav>
        </ScrollArea>

        <div className="flex shrink-0 flex-col items-center gap-2 border-t border-slate-200 p-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label={subnavCollapsed ? '展開子選單' : '收合子選單'}
            title={subnavCollapsed ? '展開子選單' : '收合子選單'}
            onClick={() => setSubnavCollapsed((current) => !current)}
          >
            {subnavCollapsed ? (
              <PanelLeftOpen className="size-4" />
            ) : (
              <PanelLeftClose className="size-4" />
            )}
          </Button>
          <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="登出" title="登出">
            <LogOut className="size-4" />
          </Button>
        </div>
      </aside>

      <aside
        className={cn(
          'relative hidden h-dvh min-w-0 border-r border-slate-200 bg-white transition-[opacity,width] duration-200 ease-out lg:flex lg:min-h-0 lg:flex-col',
          subnavCollapsed && 'pointer-events-none border-r-0 opacity-0',
        )}
        aria-hidden={subnavCollapsed}
      >
        <div className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-slate-950">{activeGroup.label}</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label="收合子選單"
            title="收合子選單"
            onClick={() => setSubnavCollapsed(true)}
          >
            <PanelLeftClose className="size-4" />
          </Button>
        </div>

        <ScrollArea className="min-h-0 flex-1" viewportClassName="px-3 py-4">
          <nav className="flex flex-col gap-1">
            {activeGroup.items.map((item) => {
              const Icon = navItemIcons[item.id]
              const active = itemActive(location.pathname, item)
              return (
                <button
                  key={item.to}
                  type="button"
                  aria-current={active ? 'page' : undefined}
                  onClick={() => void navigate({ to: item.to })}
                  className={cn(
                    'flex min-h-11 w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-950',
                    active && 'border-l-[3px] border-indigo-600 bg-indigo-50 text-indigo-800',
                  )}
                >
                  <Icon className="mt-0.5 size-4 shrink-0" />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{item.label}</span>
                  </span>
                </button>
              )
            })}
          </nav>
        </ScrollArea>

        {!subnavCollapsed && (
          <button
            type="button"
            aria-label="拖曳調整子選單寬度"
            title="拖曳調整子選單寬度"
            onPointerDown={handleSubnavResizeStart}
            className="absolute -right-1 top-0 z-20 flex h-full w-2 cursor-col-resize items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            <span
              className={cn(
                'h-12 w-1 rounded-full bg-slate-300 opacity-0 transition-opacity hover:opacity-100',
                isResizingSubnav && 'bg-emerald-500 opacity-100',
              )}
            />
          </button>
        )}

        <div className="shrink-0 border-t border-slate-200 p-3">
          <button
            type="button"
            aria-label="切換工作區"
            title="切換工作區"
            className="mb-2 flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-slate-50"
          >
            <div className="grid size-9 place-items-center rounded-md bg-slate-900 text-sm font-semibold text-white">
              W
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">
                {user?.workspaceName ?? 'Demo Workspace'}
              </div>
              <div className="text-xs text-slate-500">工作區</div>
            </div>
            <ChevronDown className="size-4 text-slate-400" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="帳戶資訊"
                title="帳戶資訊"
                className="flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-slate-50"
              >
                <div className="grid size-9 place-items-center rounded-full bg-indigo-100 font-semibold text-indigo-700">
                  {user?.name?.slice(0, 1) ?? '內'}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">帳戶</div>
                </div>
                <ChevronDown className="size-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="right"
              className="w-72 p-3"
              data-testid="dashboard-account-menu"
            >
              <DropdownMenuLabel className="px-0 text-sm font-semibold text-slate-950">
                帳戶資訊
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="mx-0" />
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-xs font-medium text-slate-500">姓名</dt>
                  <dd className="mt-1 font-medium text-slate-950">{user?.name ?? '內部管理員'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-slate-500">Email</dt>
                  <dd className="mt-1 break-all text-slate-700">
                    {user?.email ?? 'admin@miao-form.case4.app'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-slate-500">角色</dt>
                  <dd className="mt-1 text-slate-700">{user?.role ?? 'member'}</dd>
                </div>
              </dl>
              <DropdownMenuSeparator className="mx-0" />
              <Button
                type="button"
                variant="ghost"
                className="mt-1 w-full justify-start gap-2 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                onClick={handleLogout}
              >
                <LogOut className="size-4" />
                登出
              </Button>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="h-[calc(100dvh-3.5rem)] min-w-0 overflow-hidden lg:h-dvh">
        <ScrollArea className="h-full" viewportClassName="min-h-full">
          <Outlet />
        </ScrollArea>
      </main>
    </div>
  )
}
